/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { flexRender, type Header, type Table } from '@tanstack/react-table';

import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import DragHandleVerticalIcon from '@atlaskit/icon/utility/migration/drag-handle-vertical--drag-handler';
import { Box, Flex, Inline, type XCSS, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive, type TooltipPrimitiveProps } from '@atlaskit/tooltip';

import { type CharlieTableProps, type TableType } from './charlie-table';
import { HeadCell } from './components/head-cell';
import { SortIcon } from './components/sort-icon';

declare module '@tanstack/table-core' {
	//@ts-expect-error suggested tanstack solution
	interface ColumnMeta {
		description?: React.ReactNode;
		sortDescription?: {
			asc: string | React.ReactNode;
			desc: string | React.ReactNode;
		};
	}
}

const HeaderDescriptionStyles = xcss({
	background: token('elevation.surface.overlay'),
	boxSizing: 'content-box',
	maxHeight: '500px',
	maxWidth: '300px',
	//TODO: move this to the children of the tooltip, not as styling for the tooltip
	//@ts-expect-error styling is applying but property does not exist for SafeCSSObject
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	ul: {
		paddingLeft: token('space.250'),
	},
	boxShadow: 'elevation.shadow.overlay',
	color: 'color.text',
	padding: 'space.300',
	borderRadius: 'border.radius.050',
	margin: 'space.050',
});

const checkboxWrapperStyle = xcss({ paddingInlineStart: 'space.100' });
const NotReorderingPaddingStyle = xcss({
	paddingInlineStart: 'space.100',
	alignItems: 'center',
});

const overflowInlineStyle = xcss({ overflow: 'hidden', alignItems: 'center' });

const ButtonFontStyles = xcss({
	overflow: 'hidden',
	flex: 1,
	textOverflow: 'ellipsis',
	color: 'color.text.subtlest',
	textAlign: 'left',
	fontWeight: 'font.weight.bold',
});
const ButtonFontCurrentSortStyles = xcss({
	color: 'color.text.brand',
});
const SortButtonStyle = css({
	width: 'fit-content',
	paddingInline: token('space.050'),
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	fill: `${token('color.text.subtlest')} !important`,
});

const SortButtonCurrentSortStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	fill: `${token('color.text.brand')} !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'.sortable-icon': {
		opacity: 1,
		transitionDelay: '150ms',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'svg, path, g': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		fill: `${token('color.text.brand')} !important`,
	},
});

const DragHandlerIconContainerStyle = xcss({
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	width: 'size.100',
});

interface CharlieTableHeaderProps<T extends object> {
	tableType?: TableType;
	enableResizing: boolean;
	charlieTableColumn: Header<T, unknown>;
	index: number;
	charlieTableState: Table<T>;
	enableColumnReorder?: boolean;
	enableRowSelection?: CharlieTableProps<T>['enableRowSelection'];
	hasWidths: boolean;
	labels: CharlieTableProps<T>['labels'];
	xcss?: XCSS;
	analyticsContext?: Record<string, any>;
	interactionName?: string;
}

/**
 * The e2e header used by the CharlieTable, intended for usage within the e2e CharlieTable component.
 */
export const CharlieTableHeader = <T extends object>({
	labels,
	tableType,
	charlieTableColumn,
	charlieTableState,
	index,
	enableResizing = false,
	enableColumnReorder,
	enableRowSelection,
	hasWidths,
	xcss: customXcss,
	analyticsContext,
	interactionName,
}: CharlieTableHeaderProps<T>) => {
	const [showIcons, setShowIcons] = React.useState<boolean>(false);
	const PragmaticHeadCellLazy = React.lazy(() =>
		import(
			/* webpackChunkName: '@atlaskit-internal_resizable-header-charlie-table' */
			'./components/pragmatic-head-cell'
		).then((module) => ({ default: module.PragmaticHeadCell })),
	);

	const Header = React.useMemo(() => {
		if (enableResizing || enableColumnReorder) {
			return (props: React.ComponentProps<typeof PragmaticHeadCellLazy>) => (
				<React.Suspense fallback={<HeadCell {...props} />}>
					<PragmaticHeadCellLazy {...props} />
				</React.Suspense>
			);
		} else {
			return HeadCell;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enableResizing, enableColumnReorder]);

	if (enableRowSelection && index === 0) {
		return (
			<HeadCell
				tableType={tableType}
				xcss={[
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					xcss({
						// @ts-expect-error-next-line - none of the zIndex ADS tokens equate to 1
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
						zIndex: charlieTableColumn.column.getIsPinned() ? 3 : undefined,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
						left: `var(--col-${charlieTableColumn.id}-left)`,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
						right: `var(--col-${charlieTableColumn.id}-right)`,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
						position: charlieTableColumn.column.getIsPinned() ? 'sticky' : 'relative',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
						paddingInlineStart: 'space.100',
					}),
					customXcss,
				]}
				width={hasWidths ? `${charlieTableColumn.getSize()}px` : undefined}
				key={charlieTableColumn.id}
			>
				<Box xcss={checkboxWrapperStyle}>
					<Checkbox
						analyticsContext={{
							componentName: 'charlieTableSortColumnButton',
							...analyticsContext,
						}}
						aria-label={labels?.selectAllRows}
						isIndeterminate={charlieTableState.getIsSomeRowsSelected()}
						isChecked={charlieTableState.getIsAllRowsSelected()}
						onClick={() => charlieTableState.toggleAllRowsSelected()}
					/>
				</Box>
			</HeadCell>
		);
	}

	const sortDirection = charlieTableColumn.column.getIsSorted();
	return (
		<Header
			tableType={tableType}
			xcss={[
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				xcss({
					// @ts-expect-error-next-line - none of the zIndex ADS tokens equate to 1
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					zIndex: charlieTableColumn.column.getIsPinned() ? 3 : undefined,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					left: `var(--col-${charlieTableColumn.id}-left)`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					right: `var(--col-${charlieTableColumn.id}-right)`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					position: charlieTableColumn.column.getIsPinned() ? 'sticky' : 'relative',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					...(enableColumnReorder
						? { paddingInlineStart: 'space.0', cursor: 'grab' }
						: {
								//overriding dom default of space.0 for first-of-type
								':first-of-type': {
									paddingInlineStart: 'space.100',
								},
							}),
				}),
				customXcss,
			]}
			onWidthChange={(width: number) => {
				charlieTableState.setColumnSizing((old) => ({
					...old,
					[charlieTableColumn.id]: width,
				}));
			}}
			{...(sortDirection && {
				sortDirection: sortDirection === 'asc' ? 'ascending' : 'descending',
			})}
			enableColumnReorder={enableColumnReorder}
			enableResizing={enableResizing}
			width={hasWidths ? `${charlieTableColumn.getSize()}px` : undefined}
			key={charlieTableColumn.id}
			id={charlieTableColumn.id}
			onMouseEnter={() => setShowIcons(true)}
			onMouseLeave={() => setShowIcons(false)}
		>
			<Tooltip
				// TODO: remove temporary fix while we figure out why this is erroring out
				component={
					React.forwardRef<HTMLDivElement, TooltipPrimitiveProps>((props, ref) => {
						const { children, ...rest } = props;
						if (ref == null) {
							return null;
						}
						return (
							<TooltipPrimitive {...rest} ref={ref}>
								<Box xcss={HeaderDescriptionStyles}>{children}</Box>
							</TooltipPrimitive>
						);
						// NOTE: We use an assertion for React 18 compatibility, it may be solvable in better ways
					}) as React.ComponentType<TooltipPrimitiveProps>
				}
				content={
					charlieTableColumn.column.columnDef.meta?.description && (
						<Box>
							<b>
								{flexRender(
									charlieTableColumn.column.columnDef.header,
									charlieTableColumn.getContext(),
								)}
							</b>
							{charlieTableColumn.column.columnDef.meta?.description}
						</Box>
					)
				}
				position="bottom"
			>
				{(tooltipProps) => {
					return charlieTableColumn.column.getCanSort() ? (
						<Box {...tooltipProps}>
							<Inline
								xcss={enableColumnReorder ? overflowInlineStyle : NotReorderingPaddingStyle}
								spread="space-between"
								{...tooltipProps}
							>
								<Inline xcss={overflowInlineStyle}>
									{enableColumnReorder && (
										<Flex xcss={DragHandlerIconContainerStyle}>
											{showIcons && (
												<DragHandleVerticalIcon
													label="Drag list item"
													color={token('color.icon')}
													LEGACY_size="small"
												/>
											)}
										</Flex>
									)}
									<Box xcss={[ButtonFontStyles, sortDirection && ButtonFontCurrentSortStyles]}>
										{flexRender(
											charlieTableColumn.column.columnDef.header,
											charlieTableColumn.getContext(),
										)}
									</Box>
								</Inline>
								<Button
									testId={`${charlieTableColumn.id}-sort-button`}
									spacing="none"
									appearance="subtle"
									{...tooltipProps}
									onClick={() => charlieTableColumn.column.toggleSorting()}
									aria-label={`${charlieTableColumn.id} column sort button`}
									interactionName={interactionName}
									analyticsContext={{
										componentName: 'charlieTableSortColumnButton',
										...analyticsContext,
									}}
									// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
									css={[SortButtonStyle, sortDirection && SortButtonCurrentSortStyle]}
								>
									<Flex xcss={DragHandlerIconContainerStyle}>
										{charlieTableColumn.column.columnDef.meta?.sortDescription ? (
											<Tooltip
												testId="sort-tooltip"
												content={`${sortDirection === 'asc' ? charlieTableColumn.column.columnDef.meta?.sortDescription.asc : charlieTableColumn.column.columnDef.meta?.sortDescription.desc}`}
											>
												{(showIcons || sortDirection) && (
													<SortIcon
														sortDirection={sortDirection ?? 'asc'}
														isCurrentSort={sortDirection ? true : false}
													/>
												)}
											</Tooltip>
										) : (
											<React.Fragment>
												{(showIcons || sortDirection) && (
													<SortIcon
														sortDirection={sortDirection ?? 'asc'}
														isCurrentSort={sortDirection ? true : false}
													/>
												)}
											</React.Fragment>
										)}
									</Flex>
								</Button>
							</Inline>
						</Box>
					) : (
						<Box {...tooltipProps}>
							<Inline xcss={enableColumnReorder ? overflowInlineStyle : NotReorderingPaddingStyle}>
								{enableColumnReorder && (
									<Flex xcss={DragHandlerIconContainerStyle}>
										{showIcons && (
											<DragHandleVerticalIcon
												label="Drag list item"
												color={token('color.icon')}
												LEGACY_size="small"
											/>
										)}
									</Flex>
								)}
								<Box xcss={ButtonFontStyles}>
									{flexRender(
										charlieTableColumn.column.columnDef.header,
										charlieTableColumn.getContext(),
									)}
								</Box>
							</Inline>
						</Box>
					);
				}}
			</Tooltip>
		</Header>
	);
};
